import { ViewTitle } from '@diligentcorp/heracles-view-title';
import {
  useHeaderStore,
  useUserContext,
} from '@issuemanager/apps/client/contexts';
import { useAdmin } from '@issuemanager/apps/client/hooks';
import { paths } from '@issuemanager/apps/client/paths';
import { WithTheme } from '@issuemanager/apps/client/theme';
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Stack,
  styled,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../../assets/logos/logo.svg';
import AdminMenu from './AdminMenu';

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasBorder',
})<WithTheme<BoxProps & { hasBorder: boolean }>>(({ theme, hasBorder }) => ({
  backgroundColor: theme.tokens.semantic.color.background.container.value,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  borderBottomColor: hasBorder
    ? theme.tokens.semantic.color.action.default.value
    : 'unset',
  borderBottomWidth: hasBorder ? 1 : 0,
  borderBottomStyle: hasBorder ? 'solid' : 'unset',
}));

const StyledViewTitleContainer = styled(Box)(() => ({
  '& .MuiTypography-root': {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
}));

const adminMenuVisibleRoutes = [paths.issues.master];

export function AppHeader() {
  const { headerProps } = useHeaderStore();
  const isAdmin = useAdmin();
  const { userRoles } = useUserContext();
  const location = useLocation();

  const showConfigBtn =
    adminMenuVisibleRoutes.includes(location.pathname.replace(/^\//, '')) &&
    (isAdmin || userRoles?.hasManagedIssueTypes);
  const showBorderBottom = !location.pathname
    .split('/')
    .includes(paths.settings.index);

  return (
    <StyledBox data-testid='app-header' hasBorder={showBorderBottom}>
      <Stack justifyContent='space-between' direction='row' alignItems='center'>
        <Stack direction='row' spacing={2} alignItems='center'>
          <StyledViewTitleContainer>
            <ViewTitle icon={<Logo />} {...headerProps.viewTitle} />
          </StyledViewTitleContainer>
          {headerProps.chip ? (
            <Stack justifyContent='center'>{headerProps.chip}</Stack>
          ) : null}
        </Stack>

        <Stack direction='row' spacing={2} alignItems='center'>
          {headerProps.buttons?.map((button) => {
            const { loading, ...rest } = button;
            return (
              <Button
                key={button.children!.toString()}
                {...rest}
                {...(loading
                  ? {
                      disabled: true,
                      startIcon: <CircularProgress size={24} />,
                      endIcon: null,
                    }
                  : {})}
                sx={{ whiteSpace: 'nowrap' }}
              />
            );
          })}

          {showConfigBtn && <AdminMenu />}
        </Stack>
      </Stack>

      {headerProps.bottomSection && (
        <Box mt={3}>{headerProps.bottomSection}</Box>
      )}
    </StyledBox>
  );
}
